import React, { useState, useEffect, useImperativeHandle } from "react";
import Editor from "@monaco-editor/react";
import UnifiedConsole from "./UnifiedConsole";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import SubmitModal from "./SubmitModal";
import { ChevronDown } from "lucide-react";
import useExecutorHook from "./useExecutorHook";

type SupportedLanguage = "javascript" | "python" | "java" | "c" | "cpp";

export type OutputItem = {
  type: "log" | "error" | "result";
  content: string;
};

export interface CodeEditorRef {
  submitUserCode: () => void;
}

interface CodeEditorProps {
  placeholder: string;
  language: SupportedLanguage;
  onSubmit: (code: string) => void;
  testCases: { input: string; expectedOutput: string; description: string; fullExecutableCode?: string }[];
  setSelectedLanguage: (language: SupportedLanguage) => void;
  handleDsaQuestionSubmit: (response: any) => void;
  onUserSubmit?: () => void;
  selectedLanguage: SupportedLanguage;
  /** When true, the language selector dropdown is disabled */
  languageDisabled?: boolean;
  /** When true, show a processing state while switching languages */
  isLanguageSwitching?: boolean;
  /** Optional container element for the portal (useful for fullscreen mode) */
  portalContainer?: HTMLElement | null;
}

const CodeEditor = React.forwardRef<CodeEditorRef, CodeEditorProps>(
  (
    {
      placeholder,
      language,
      onSubmit,
      testCases,
      setSelectedLanguage,
      handleDsaQuestionSubmit,
      onUserSubmit,
      languageDisabled = false,
      isLanguageSwitching = false,
      portalContainer,
    },
    ref
  ) => {
    const [code, setCode] = useState<string>(placeholder);
    const [output, setOutput] = useState<OutputItem[]>([]);
    const [testsExecuted, setTestsExecuted] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [testResults, setTestResults] = useState<any[]>([]);
    const [activeConsoleTab, setActiveConsoleTab] = useState<"console" | "testcase">("console");
    const [codeAfterTest, setCodeAfterTest] = useState<string>("");

    const { executeCode, result: hookResult, isLoading: isExecLoading } = useExecutorHook();

    // Update code when placeholder changes (when language changes)
    useEffect(() => {
      setCode(placeholder);
      // Reset test state when language changes
      setTestsExecuted(false);
      setTestResults([]);
      setCodeAfterTest("");
      setActiveConsoleTab("console");
    }, [placeholder]);

    // Track code changes after tests have been executed
    useEffect(() => {
      if (testsExecuted && code !== codeAfterTest) {
        // Code has changed after tests were executed, invalidate tests
        setTestsExecuted(false);
        setTestResults([]);
        setActiveConsoleTab("console");
      }
    }, [code, testsExecuted, codeAfterTest]);

    // When hook returns results, reflect into local console/test UI
    useEffect(() => {
      if (testsExecuted && hookResult) {
        setTestResults(hookResult);
        setActiveConsoleTab("testcase");
      }
    }, [hookResult, testsExecuted]);

    const handleEditorChange = (value: string | undefined) => {
      if (value !== undefined) {
        setCode(value);
      }
    };

    const mapTestCasesForHook = (cases: { input: string; expectedOutput: string; fullExecutableCode?: string }[]) =>
      cases.map((tc) => ({ input: tc.input, expectedOutput: tc.expectedOutput, fullExecutableCode: tc.fullExecutableCode ?? '' }));

    const handleRun = async () => {
      setActiveConsoleTab("console");
      setTestsExecuted(false);
      if (code.trim() === "") {
        setOutput([{ type: "error", content: "Please write some code first." }]);
        return;
      }
      try {
        setOutput([]);
        const first = testCases?.[0] ? [testCases[0]] : [];

        const result = await executeCode({ userCode: code, language, testCases: mapTestCasesForHook(first) });
        const firstResult = (result && result.length > 0) ? result[0] : null;
        const isError = !!(firstResult?.error || firstResult?.compilationError || firstResult?.runtimeError);
        const content = isError
          ? (firstResult?.error || firstResult?.compilationError || firstResult?.runtimeError || "Error")
          : `Result: ${firstResult?.actualOutput ?? ""}`;
        setOutput([
          {
            type: isError ? "error" : "result",
            content,
          },
        ]);
      } catch (e: any) {
        setOutput([{ type: "error", content: e?.message || "Run failed" }]);
      }
    };

    // Test button - runs all test cases
    const handleTest = async () => {
      setActiveConsoleTab("testcase");
      setTestsExecuted(true);
      setCodeAfterTest(code);
      const results = await executeCode({ userCode: code, language, testCases: mapTestCasesForHook(testCases || []) });
      if (results) {
        setTestResults(results);
      }
    };

    // Submit button - always available, shows dialog if tests not all passed
    const handleSubmit = async () => {
      let resultsToUse = testResults;
      
      // First run tests if not already executed or if code changed
      if (!testsExecuted || code !== codeAfterTest) {
        setActiveConsoleTab("testcase");
        setTestsExecuted(true);
        setCodeAfterTest(code);
        const results = await executeCode({ userCode: code, language, testCases: mapTestCasesForHook(testCases || []) });
        resultsToUse = results || [];
        setTestResults(resultsToUse);
      }
      
      // Check if all tests passed (handle empty test cases gracefully)
      const hasTests = resultsToUse && resultsToUse.length > 0;
      const allPassed = hasTests && resultsToUse.every(r => r.status === 'pass');
      
      if (allPassed) {
        // All tests passed - submit directly without dialog
        console.log('[CodeEditor] All tests passed, submitting directly');
        handleDsaQuestionSubmit({
          code,
          testCases: resultsToUse,
        });
        onUserSubmit?.();
      } else {
        // Not all tests passed (or no tests) - show confirmation dialog
        console.log('[CodeEditor] Not all tests passed, showing confirmation dialog');
        setIsModalOpen(true);
      }
    };

    const submitUserCode = () => {
      handleDsaQuestionSubmit({
        code,
        testCases: testResults,
      });
      onUserSubmit?.();
    };

    useImperativeHandle(ref, () => {
      return {
        submitUserCode,
      };
    });

    return (
      <div className="flex flex-col h-full">
        {/* Editor Section - 70% height */}
        <div className="flex-[7] min-h-0 rounded-lg overflow-hidden">
          <div className="bg-[#000000] px-3.5 py-2.5 rounded-t-lg flex flex-row items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-[#24D680] text-sm">Language :</span>
              <span className="text-white text-sm">
                {languageDisabled ? (
                  <span className="opacity-70">{language}</span>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center gap-1 transition-opacity hover:opacity-80 cursor-pointer outline-none">
                      <span>{language}</span>
                      <ChevronDown className="w-4 h-4 text-white" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent container={portalContainer ?? undefined}>
                      <DropdownMenuItem onClick={() => setSelectedLanguage("javascript")}>JavaScript</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedLanguage("python")}>Python</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedLanguage("java")}>Java</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedLanguage("c")}>C</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedLanguage("cpp")}>C++</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRun}
                className="text-[#24D680] text-sm font-medium hover:text-[#05733E] disabled:opacity-50 disabled:hover:text-[#24D680]"
                disabled={languageDisabled}
              >
                Run
              </button>
              <button
                onClick={handleTest}
                className="text-[#24D680] text-sm font-medium hover:text-[#05733E] disabled:opacity-50 disabled:hover:text-[#24D680]"
                disabled={languageDisabled}
              >
                Test
              </button>
              <button
                onClick={handleSubmit}
                className="text-[#24D680] text-sm font-medium hover:text-[#05733E] disabled:opacity-50 disabled:hover:text-[#24D680]"
                disabled={languageDisabled}
              >
                Submit
              </button>
            </div>
          </div>
          <div className="h-[calc(100%-48px)] rounded-b-lg overflow-hidden relative">
            <Editor
              height="100%"
              defaultLanguage={language === "javascript" ? "javascript" : "python"}
              language={language}
              value={code}
              onChange={handleEditorChange}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: "on",
                automaticLayout: true,
                padding: { top: 16, bottom: 16 },
                folding: false,
                matchBrackets: "never",
                renderValidationDecorations: "off",
                readOnly: languageDisabled,
                scrollbar: {
                  vertical: "visible",
                  horizontal: "visible",
                  verticalScrollbarSize: 6,
                  horizontalScrollbarSize: 6,
                  arrowSize: 8,
                },
              }}
            />
            {isLanguageSwitching && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm">
                Processing...
              </div>
            )}
            {/* Monaco Editor Scrollbar Styling */}
            <style>
              {`
                .monaco-editor .monaco-scrollable-element > .scrollbar > .slider {
                  background: #4B4B4B !important;
                  border-radius: 3px !important;
                }
                .monaco-editor .monaco-scrollable-element > .scrollbar > .slider:hover {
                  background: #5B5B5B !important;
                }
                .monaco-editor .monaco-scrollable-element > .scrollbar {
                  background: transparent !important;
                }
                .monaco-editor .monaco-scrollable-element > .scrollbar.horizontal > .slider {
                  height: 4px !important;
                }
                .monaco-editor .monaco-scrollable-element > .scrollbar.vertical > .slider {
                  width: 6px !important;
                }
              `}
            </style>
          </div>
        </div>

        {/* Console Section - 30% height */}
        <div className="flex-[3] min-h-0 mt-2">
          <UnifiedConsole
            output={output}
            testCases={testCases}
            code={code}
            language={language?.toLowerCase()}
            functionName={""}
            setTestResults={setTestResults}
            activeTab={activeConsoleTab}
            onTabChange={setActiveConsoleTab}
            testsExecuted={testsExecuted}
            externalResults={(testsExecuted ? (hookResult || []) : []).map(r => ({
              input: r.input,
              expectedOutput: r.expectedOutput,
              actualOutput: r.actualOutput,
              status: r.status,
            }))}
            isLoading={isExecLoading}
          />
        </div>

        {isModalOpen && (
          <SubmitModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onRefresh={async () => setIsModalOpen(false)}
            onSubmit={() => {
              console.log('[CodeEditor] Modal submit confirmed, submitting code');
              setIsModalOpen(false);
              handleDsaQuestionSubmit({
                code,
                testCases: testResults,
              });
              onUserSubmit?.();
            }}
            portalContainer={portalContainer}
          />
        )}
      </div>
    );
  }
);

export default CodeEditor;
